import { useState, useEffect } from 'react';

/**
 * Manages a local string buffer for a controlled number input.
 *
 * Keeps a string copy so that partially-typed values like `-`, `1.`, or `-0.`
 * are preserved while the user is mid-entry, while still calling `onChange`
 * every time a fully valid number is parseable. On blur, any string that still
 * doesn't parse (e.g. a lone `-`) is reset to the current prop value.
 *
 * Sync direction: prop → local only when the parsed value differs, so an
 * in-progress edit like `"1."` (parses as `1`) is not clobbered when the
 * parent re-renders with `value={1}`.
 */
export function useLocalNumber(value: number, onChange: (n: number) => void) {
  const [local, setLocal] = useState(String(value));

  useEffect(() => {
    setLocal((prev) => (parseFloat(prev) !== value ? String(value) : prev));
  }, [value]);

  const handleChange = (raw: string) => {
    setLocal(raw);
    const n = parseFloat(raw);
    if (!isNaN(n)) onChange(n);
  };

  const handleBlur = () => {
    if (isNaN(parseFloat(local))) setLocal(String(value));
  };

  return { local, handleChange, handleBlur };
}
